import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ArrowPathIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentCheckIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'

import Layout from '../../components/layout/Layout'
import {
  Badge,
  Button,
  Input,
  Label,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui'
import { cn } from '../../shared/lib/cn'
import { useToasts } from '../../stores/useToasts'
import {
  useContractContext,
  useExtractGmailSowAttachment,
  useGmailSowAttachmentPreview,
  useScanGmailSowIntake,
  useSyncContractContextToGoogleSheets,
  useUploadManualSowPdf,
} from './api'
import type {
  ContractContext,
  EditableApprovedSowData,
  GmailSowExtractResponse,
  GmailSowIntakeResponse,
} from './types'
import OperatingTestPanel from './features/operating/OperatingTestPanel'
import { RunTracePanel, WorkflowTracker } from './features/workflow/WorkflowPanels'
import { GmailIntakePanel, ManualSowUploadPanel } from './features/intake/SowIntakePanels'
import { GoogleSheetsSyncPanel } from './features/integrations/IntegrationPanels'
import { SowExtractionReviewSection, SOWDistributionPanel } from './features/sow/SowReviewWorkspace'
import { ContractDetail, HubSpotDealsTable } from './features/contracts/ContractDataPanels'
import { StatusBadge } from './components/ContractBadges'
import {
  buildApprovedSowDataFromExtraction,
  buildContextFromApprovedSow,
  buildContextSowData,
  buildWorkflowRuns,
  buildWorkflowSteps,
  type GmailSowAttachment,
  type OperatingWorkflowUpdate,
} from './domain/contractWorkflow'
import {
  formatCurrency,
  getErrorMessage,
  getGmailScanErrorMessage,
  lifecycleValue,
} from './domain/formatters'


function MetricTile({
  label,
  value,
  icon: Icon,
}: {
  label: string
  value: string
  icon: React.ComponentType<{ className?: string }>
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
          {label}
        </span>
        <Icon className="h-4 w-4 text-gray-400" />
      </div>
      <p className="mt-3 truncate text-2xl font-semibold text-charcoal dark:text-gray-100">
        {value}
      </p>
    </div>
  )
}

export default function ContractAutomationPage() {
  const pushToast = useToasts(state => state.push)
  const [clientInput, setClientInput] = useState('')
  const [searchedClient, setSearchedClient] = useState('')
  const [selectedRunId, setSelectedRunId] = useState<string | undefined>()
  const [gmailQuery, setGmailQuery] = useState('has:attachment filename:pdf newer_than:30d')
  const [gmailExtraction, setGmailExtraction] = useState<GmailSowExtractResponse | undefined>()
  const [approvedSowData, setApprovedSowData] = useState<EditableApprovedSowData | undefined>()
  const [operatingWorkflowUpdate, setOperatingWorkflowUpdate] = useState<OperatingWorkflowUpdate>({ status: 'idle' })
  const handleOperatingWorkflowUpdate = useCallback((update: OperatingWorkflowUpdate) => {
    setOperatingWorkflowUpdate(current =>
      current.status === update.status && current.message === update.message ? current : update,
    )
  }, [])
  const [extractedGmailAttachment, setExtractedGmailAttachment] = useState<GmailSowAttachment | undefined>()
  const [manualSowFile, setManualSowFile] = useState<File | undefined>()
  const contextQuery = useContractContext(searchedClient, Boolean(searchedClient))
  const gmailScan = useScanGmailSowIntake()
  const extractGmailSow = useExtractGmailSowAttachment()
  const uploadManualSow = useUploadManualSowPdf()
  const gmailAttachmentPreview = useGmailSowAttachmentPreview(
    extractedGmailAttachment?.local_path,
    extractedGmailAttachment?.filename,
    Boolean(extractedGmailAttachment?.local_path),
  )
  const syncSheets = useSyncContractContextToGoogleSheets()
  const context = contextQuery.data
  const contracts = useMemo(() => context?.contracts || [], [context])
  const deals = useMemo(() => context?.deals || [], [context])
  const hubspotDeals = useMemo(
    () => gmailExtraction?.context.deals?.length
      ? gmailExtraction.context.deals
      : deals,
    [gmailExtraction, deals],
  )
  const lifecycle = useMemo(() => context?.lifecycle || {}, [context])
  const latestSowEmail = useMemo(
    () => gmailScan.data?.messages.filter(message => message.matched)[0],
    [gmailScan.data],
  )
  const approvedContext = useMemo(
    () => approvedSowData ? buildContextFromApprovedSow(approvedSowData) : gmailExtraction?.context || context,
    [approvedSowData, gmailExtraction, context],
  )
  const latestEmailWorkflowContext = useMemo<ContractContext | undefined>(() => {
    if (!latestSowEmail || approvedContext || context) return undefined
    return {
      client_name: latestSowEmail.subject || 'Latest SOW email',
      contracts: [],
      deals: [],
      lifecycle: { sow: 'unknown', stage: 'unknown', msa: 'unknown', payment: 'unknown' },
      summary: 'Latest signed SOW email found in Gmail. Extraction is pending.',
    }
  }, [latestSowEmail, approvedContext, context])
  const workflowContext = approvedContext || context || latestEmailWorkflowContext
  const workflowSteps = useMemo(
    () => buildWorkflowSteps(workflowContext, syncSheets, latestSowEmail, operatingWorkflowUpdate),
    [workflowContext, syncSheets, latestSowEmail, operatingWorkflowUpdate],
  )
  const workflowRuns = useMemo(
    () => buildWorkflowRuns(workflowContext, workflowSteps),
    [workflowContext, workflowSteps],
  )
  const selectedRun = useMemo(
    () => workflowRuns.find(run => run.id === selectedRunId) || workflowRuns[0],
    [selectedRunId, workflowRuns],
  )
  const selectedWorkflowSteps = selectedRun?.steps || workflowSteps
  const selectedSowData = approvedSowData || (context ? selectedRun?.sowData || buildContextSowData(context) : undefined)
  const selectedExtractionCitations = useMemo(
    () => gmailExtraction?.extraction?.citations || context?.contracts?.[0]?.extraction?.citations || [],
    [gmailExtraction, context],
  )
  const parsedGmailAttachment = useMemo(() => {
    if (!extractedGmailAttachment || !gmailScan.data) return undefined
    const cameFromCurrentScan = gmailScan.data.messages.some(message =>
      message.downloaded_attachments.some(attachment => attachment.local_path === extractedGmailAttachment.local_path),
    )
    return cameFromCurrentScan ? extractedGmailAttachment : undefined
  }, [extractedGmailAttachment, gmailScan.data])

  useEffect(() => {
    if (workflowRuns.length === 0) return
    if (!selectedRunId || !workflowRuns.some(run => run.id === selectedRunId)) {
      setSelectedRunId(workflowRuns[0].id)
    }
  }, [selectedRunId, workflowRuns])

  const totalForecastedRevenue = useMemo(
    () => contracts.reduce((sum, row) => sum + (row.extraction?.contract_value || 0), 0),
    [contracts],
  )
  const withScheduledDates = useMemo(
    () => contracts.filter(row => row.extraction?.start_date && row.extraction?.end_date).length,
    [contracts],
  )
  const search = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmed = clientInput.trim()
    if (trimmed) setSearchedClient(trimmed)
  }

  const extractAttachment = (attachment: GmailSowIntakeResponse['messages'][number]['downloaded_attachments'][number]) => {
    if (!attachment.local_path) {
      pushToast({ kind: 'warning', message: 'Attachment has no downloaded file path.' })
      return
    }
    extractGmailSow.mutate(
      {
        localPath: attachment.local_path,
        filename: attachment.filename,
      },
      {
        onSuccess: extraction => {
          setGmailExtraction(extraction)
          setApprovedSowData(buildApprovedSowDataFromExtraction(extraction))
          setExtractedGmailAttachment(attachment)
          pushToast({ kind: 'success', message: 'SOW PDF extracted for approval.' })
        },
        onError: error => pushToast({ kind: 'error', message: getErrorMessage(error) }),
      },
    )
  }

  const uploadManualSowPdf = () => {
    if (!manualSowFile) return
    if (!manualSowFile.name.toLowerCase().endsWith('.pdf') && manualSowFile.type !== 'application/pdf') {
      pushToast({ kind: 'warning', message: 'Upload a PDF Statement of Work.' })
      return
    }
    uploadManualSow.mutate(
      { file: manualSowFile },
      {
        onSuccess: extraction => {
          const attachment = extraction.attachment || {
            filename: extraction.file_name,
            mime_type: extraction.mime_type,
            size_bytes: manualSowFile.size,
            local_path: '',
          }
          setGmailExtraction(extraction)
          setApprovedSowData(buildApprovedSowDataFromExtraction(extraction))
          setExtractedGmailAttachment(attachment)
          pushToast({ kind: 'success', message: 'Manual SOW PDF uploaded and parsed for approval.' })
        },
        onError: error => pushToast({ kind: 'error', message: getErrorMessage(error) }),
      },
    )
  }

  const kickOffGmailScan = () => {
    const query = gmailQuery.trim() || 'has:attachment filename:pdf newer_than:30d'
    setGmailExtraction(undefined)
    setApprovedSowData(undefined)
    setExtractedGmailAttachment(undefined)
    gmailScan.mutate(
      { query, maxMessages: 25, download: true },
      {
        onSuccess: result => {
          pushToast({
            kind: result.matched_count > 0 ? 'success' : 'info',
            message: `Gmail scan complete: ${result.matched_count} SOW candidate${result.matched_count === 1 ? '' : 's'} found.`,
          })
        },
        onError: () => {
          pushToast({ kind: 'error', message: getGmailScanErrorMessage() })
        },
      },
    )
  }

  const resetExtractedSow = () => {
    setGmailExtraction(undefined)
    setApprovedSowData(undefined)
    setExtractedGmailAttachment(undefined)
    setOperatingWorkflowUpdate({ status: 'idle' })
  }

  return (
    <Layout>
      <div className="h-full overflow-auto bg-gray-50 dark:bg-dark-bg">
        <div className="mx-auto flex max-w-[1320px] flex-col gap-6 px-4 py-6 sm:px-6 lg:py-8">
          <header className="rounded-3xl border border-gray-100 bg-white p-6 shadow-card dark:border-dark-border/30 dark:bg-dark-surface lg:p-7">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Badge variant="default">Revenue & Cash Forecasting</Badge>
                  <Badge variant="secondary">HubSpot</Badge>
                  <Badge variant="secondary">Drive SOWs</Badge>
                  <Badge variant="secondary">Slack approvals</Badge>
                </div>
                <h1 className="font-title text-3xl text-charcoal dark:text-gray-100">
                  Contract Automation
                </h1>
                <p className="mt-3 text-sm leading-6 text-gray-500 dark:text-gray-400">
                  Scan DocuSign emails for signed SOWs, parse the PDF, approve the contract fields, and sync HubSpot, Operating, and revenue spreadsheets.
                </p>
              </div>

              <div className="flex w-full flex-col gap-3 lg:max-w-xl">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-bg">
                  <Label htmlFor="gmail-intake-query" className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                    Gmail intake query
                  </Label>
                  <div className="mt-2 flex w-full flex-col gap-2 sm:flex-row">
                    <Input
                      id="gmail-intake-query"
                      value={gmailQuery}
                      onChange={event => setGmailQuery(event.target.value)}
                      placeholder="has:attachment filename:pdf newer_than:30d"
                    />
                    <Button
                      type="button"
                      variant="brand"
                      onClick={kickOffGmailScan}
                      disabled={gmailScan.isPending}
                    >
                      {gmailScan.isPending ? <Spinner size="sm" /> : <EnvelopeIcon className="h-4 w-4" />}
                      Scan Gmail
                    </Button>
                  </div>
                </div>
                <form onSubmit={search} className="flex w-full flex-col gap-2 sm:flex-row">
                  <div className="min-w-0 flex-1">
                    <Label htmlFor="contract-client-search" className="sr-only">Client name</Label>
                    <Input
                      id="contract-client-search"
                      value={clientInput}
                      onChange={event => setClientInput(event.target.value)}
                      placeholder="Search client name"
                    />
                  </div>
                  <Button type="submit" variant="brand" disabled={!clientInput.trim() || contextQuery.isFetching}>
                    {contextQuery.isFetching ? <Spinner size="sm" /> : <MagnifyingGlassIcon className="h-4 w-4" />}
                    Search
                  </Button>
                </form>
              </div>
            </div>
          </header>

          <ManualSowUploadPanel
            file={manualSowFile}
            isPending={uploadManualSow.isPending}
            error={uploadManualSow.isError ? uploadManualSow.error : null}
            onFileChange={setManualSowFile}
            onUpload={uploadManualSowPdf}
          />

          <GmailIntakePanel
            result={gmailScan.data}
            isPending={gmailScan.isPending}
            error={gmailScan.isError ? gmailScan.error : null}
            isExtracting={extractGmailSow.isPending}
            parsedAttachment={parsedGmailAttachment}
            onExtractAttachment={extractAttachment}
            onResetExtraction={resetExtractedSow}
          />

          {(extractGmailSow.isPending || uploadManualSow.isPending) && (
            <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
              <div className="flex items-center gap-3">
                <Spinner size="sm" />
                <div>
                  <h2 className="text-base font-semibold text-charcoal dark:text-gray-100">Extracting SOW approval fields</h2>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Reading the downloaded PDF and populating FDEs, allocations, contract value, and contract length.
                  </p>
                </div>
              </div>
            </section>
          )}
          <SowExtractionReviewSection
            selectedSowData={selectedSowData}
            selectedRun={selectedRun}
            citations={selectedExtractionCitations}
            onChange={setApprovedSowData}
            attachment={extractedGmailAttachment}
            previewBlob={gmailAttachmentPreview.data}
            previewLoading={gmailAttachmentPreview.isFetching}
            previewError={gmailAttachmentPreview.isError ? gmailAttachmentPreview.error : null}
          />

          {selectedSowData && (
            <SOWDistributionPanel
              approvedData={selectedSowData}
              approvedContext={approvedContext}
              hubspotDeals={hubspotDeals}
              sourceAttachment={extractedGmailAttachment}
              onApprovedDataChange={setApprovedSowData}
              onOperatingUpdate={handleOperatingWorkflowUpdate}
            />
          )}
          <WorkflowTracker steps={selectedWorkflowSteps} />

          {!searchedClient && !gmailScan.data && !selectedSowData && (
            <section className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center dark:border-dark-border dark:bg-dark-surface">
              <EnvelopeIcon className="mx-auto h-10 w-10 text-gray-400" />
              <h2 className="mt-4 text-lg font-semibold text-charcoal dark:text-gray-100">
                Ready for SOW intake
              </h2>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Scan Gmail for a signed SOW or upload a PDF to begin review and distribution.
              </p>
            </section>
          )}

          {searchedClient && contextQuery.isLoading && (
            <section className="flex min-h-[280px] items-center justify-center rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
              <div className="text-center">
                <Spinner size="lg" className="mx-auto" />
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading contract data for {searchedClient}...</p>
              </div>
            </section>
          )}

          {contextQuery.isError && (
            <section className="rounded-lg border border-rose-200 bg-rose-50 p-5 text-rose-800 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5" />
                <div>
                  <h2 className="font-semibold">Contract context failed to load</h2>
                  <p className="mt-1 text-sm">{getErrorMessage(contextQuery.error)}</p>
                </div>
              </div>
            </section>
          )}

          {context && (
            <>
              <div className="grid gap-4 lg:grid-cols-3">
                <MetricTile
                  label="Total Forecasted Revenue"
                  value={formatCurrency(totalForecastedRevenue)}
                  icon={BanknotesIcon}
                />
                <MetricTile
                  label="Contracts Loaded"
                  value={String(contracts.length)}
                  icon={DocumentCheckIcon}
                />
                <MetricTile
                  label="With Scheduled Dates"
                  value={String(withScheduledDates)}
                  icon={ClockIcon}
                />
              </div>

              <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-base font-semibold text-charcoal dark:text-gray-100">Engagement lifecycle</h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Current source-backed status for {context.client_name}.
                    </p>
                  </div>
                  <Button variant="secondary" size="sm" onClick={() => contextQuery.refetch()} disabled={contextQuery.isFetching}>
                    <ArrowPathIcon className={cn('h-4 w-4', contextQuery.isFetching && 'animate-spin')} />
                    Refresh
                  </Button>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {[
                    ['Stage', lifecycleValue(lifecycle, 'stage')],
                    ['MSA', lifecycleValue(lifecycle, 'msa')],
                    ['SOW', lifecycleValue(lifecycle, 'sow')],
                    ['Payment', lifecycleValue(lifecycle, 'payment')],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-bg">
                      <p className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{label}</p>
                      <div className="mt-2"><StatusBadge value={value} /></div>
                    </div>
                  ))}
                </div>
                {lifecycleValue(lifecycle, 'payment') === 'unknown' && (
                  <p className="mt-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <LinkIcon className="h-4 w-4" />
                    Payment status requires QuickBooks and will populate when connected.
                  </p>
                )}
              </section>

              <Tabs defaultValue="contracts">
                <TabsList>
                  <TabsTrigger value="contracts">Contracts</TabsTrigger>
                  <TabsTrigger value="hubspot">HubSpot Deals</TabsTrigger>
                  <TabsTrigger value="sheets">Sheets Sync</TabsTrigger>
                  <TabsTrigger value="summary">Summary</TabsTrigger>
                  <TabsTrigger value="operating-test">SOW → Operating (test)</TabsTrigger>
                </TabsList>

                <TabsContent value="contracts" className="mt-4">
                  {contracts.length === 0 ? (
                    <section className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center dark:border-dark-border dark:bg-dark-surface">
                      <p className="text-sm font-medium text-charcoal dark:text-gray-100">
                        No contracts found for {context.client_name}
                      </p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Check the Drive connector folder or try another client name.
                      </p>
                    </section>
                  ) : (
                    <div className="space-y-4">
                      {contracts.map(contract => (
                        <ContractDetail key={contract.file_name} contract={contract} />
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="hubspot" className="mt-4">
                  <HubSpotDealsTable deals={deals} />
                </TabsContent>

                <TabsContent value="sheets" className="mt-4">
                  <GoogleSheetsSyncPanel context={context} syncSheets={syncSheets} />
                </TabsContent>

                <TabsContent value="summary" className="mt-4">
                  <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
                    <div className="flex items-start gap-3">
                      <CheckCircleIcon className="mt-0.5 h-5 w-5 text-gray-400" />
                      <div>
                        <h2 className="text-base font-semibold text-charcoal dark:text-gray-100">AI summary</h2>
                        <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-gray-600 dark:text-gray-300">
                          {context.summary || 'No summary returned for this contract search.'}
                        </p>
                      </div>
                    </div>
                  </section>
                </TabsContent>

                <TabsContent value="operating-test" className="mt-4">
                  <OperatingTestPanel />
                </TabsContent>
              </Tabs>
            </>
          )}

          <RunTracePanel
            runs={workflowRuns}
            selectedRunId={selectedRun?.id}
            onSelectRun={setSelectedRunId}
          />
        </div>
      </div>
    </Layout>
  )
}
